import React, { useEffect, useState } from 'react';
import { useAdminTheme } from '../context/AdminThemeContext';
import { Upload, Trash2, FileText, Loader2, List, PlusCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Notification from '../../Component/Notification';

const SyllabusPage: React.FC = () => {
  const { colors } = useAdminTheme();
  
  // 'upload' or 'list'
  const [activeTab, setActiveTab] = useState<'upload' | 'list'>('list'); 
  
  const [files, setFiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [notify, setNotify] = useState<{ isOpen: boolean; message: string; type: 'success' | 'error' }>({
    isOpen: false, message: '', type: 'success'
  });

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    setLoading(true);
    try {
      // const res = await fetch('http://localhost:2025/api/admin/syllabus');
      const res = await fetch('https://www.projectinfotech.in/api/admin/syllabus');
      if (res.ok) setFiles(await res.json());
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const formData = new FormData();
      formData.append('file', file);

      setUploading(true);
      try {
        // const res = await fetch('http://localhost:2025/api/admin/syllabus/upload', {
        const res = await fetch('https://www.projectinfotech.in/api/admin/syllabus/upload', {
          method: 'POST',
          body: formData
        });
        if (res.ok) {
          setNotify({ isOpen: true, message: 'File uploaded successfully!', type: 'success' });
          fetchFiles();
          setTimeout(() => setActiveTab('list'), 1000); // Auto switch to list after upload
        } else {
          setNotify({ isOpen: true, message: 'Upload failed', type: 'error' });
        }
      } catch (err) {
        setNotify({ isOpen: true, message: 'Server error', type: 'error' });
      } finally {
        setUploading(false);
      }
    }
  };

  const handleDelete = async (id: number) => {
    if(!window.confirm("Delete this file permanently?")) return;
    try {
      await fetch(`https://www.projectinfotech.in/api/admin/syllabus/${id}`, { method: 'DELETE' });
      // await fetch(`http://localhost:2025/api/admin/syllabus/${id}`, { method: 'DELETE' });
      setFiles(files.filter(f => f.id !== id));
      setNotify({ isOpen: true, message: 'File deleted', type: 'success' });
    } catch (err) {
      setNotify({ isOpen: true, message: 'Delete failed', type: 'error' });
    }
  };

  return (
    <div className="animate-fade-in-up max-w-5xl mx-auto">
      <Notification isOpen={notify.isOpen} message={notify.message} type={notify.type} onClose={() => setNotify({ ...notify, isOpen: false })} />
      
      <div className="flex flex-col md:flex-row justify-between items-end mb-8 border-b border-gray-200 pb-4">
        <div>
            <h2 className={`text-2xl font-bold ${colors.navbarText}`}>Syllabus Manager</h2>
            <p className="text-sm opacity-60 mt-1">Upload and manage course PDFs</p>
        </div>

        {/* TABS BUTTONS */}
        <div className="flex space-x-2 mt-4 md:mt-0">
            <button 
                onClick={() => setActiveTab('list')}
                className={`flex items-center gap-2 px-5 py-2 rounded-lg font-medium transition-all
                    ${activeTab === 'list' ? colors.button3D + ' text-white shadow-lg' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}
                `}
            >
                <List size={18} /> View List
            </button>
            <button 
                onClick={() => setActiveTab('upload')}
                className={`flex items-center gap-2 px-5 py-2 rounded-lg font-medium transition-all
                    ${activeTab === 'upload' ? colors.button3D + ' text-white shadow-lg' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}
                `}
            >
                <PlusCircle size={18} /> Upload New
            </button>
        </div>
      </div>

      <div className={`rounded-xl shadow-sm border p-6 min-h-[400px] ${colors.cardBg} ${colors.cardText} ${colors.borderColor}`}>
        
        {/* --- TAB 1: UPLOAD AREA --- */}
        {activeTab === 'upload' && (
            <motion.div 
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className="flex flex-col items-center justify-center h-80 border-2 border-dashed border-gray-300 rounded-2xl bg-gray-50/50 hover:bg-gray-50 transition-colors"
            >
                <div className={`p-4 rounded-full mb-4 ${colors.mainBg}`}>
                    <Upload size={40} className={colors.accentColor} />
                </div>
                <h3 className="text-xl font-bold mb-2">Upload Syllabus PDF</h3>
                <p className="text-sm text-gray-500 mb-6">Supported format: .pdf only</p>
                
                <div className="relative">
                    <input 
                        type="file" accept=".pdf" id="file-upload" className="hidden" 
                        onChange={handleUpload} 
                    />
                    <label 
                        htmlFor="file-upload" 
                        className={`flex items-center gap-2 px-8 py-3 rounded-xl cursor-pointer text-white font-bold transition-transform active:scale-95 shadow-md ${colors.button3D}`}
                    >
                        {uploading ? <Loader2 className="animate-spin" /> : <Upload size={20} />}
                        {uploading ? "Uploading..." : "Select File"}
                    </label>
                </div>
            </motion.div>
        )}

        {/* --- TAB 2: LIST AREA --- */}
        {activeTab === 'list' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {files.map((file) => (
                    <motion.div 
                        key={file.id} 
                        whileHover={{ y: -5 }}
                        className={`p-4 rounded-xl shadow-sm border flex items-center justify-between transition-all ${colors.cardBg} ${colors.borderColor} hover:shadow-md`}
                    >
                        <div className="flex items-center gap-4 overflow-hidden">
                            <div className="p-3 bg-red-50 text-red-600 rounded-lg shrink-0">
                                <FileText size={24} />
                            </div>
                            <div className="min-w-0">
                                <p className="font-semibold truncate text-sm" title={file.fileName}>{file.fileName}</p>
                                <p className="text-xs opacity-50 mt-1">
                                    {new Date(file.uploadedAt).toLocaleDateString()}
                                </p>
                            </div>
                        </div>
                        <button 
                            onClick={() => handleDelete(file.id)} 
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                            title="Delete File"
                        >
                            <Trash2 size={18} />
                        </button>
                    </motion.div>
                    ))}
                </div>

                {files.length === 0 && !loading && (
                    <div className="flex flex-col items-center justify-center h-64 opacity-50">
                        <FileText size={48} className="mb-4 text-gray-300" />
                        <p>No syllabus files found.</p>
                        <button onClick={() => setActiveTab('upload')} className="text-red-500 font-bold text-sm mt-2 hover:underline">
                            Upload one now
                        </button>
                    </div>
                )}
            </motion.div>
        )}
      </div>
    </div>
  );
};

export default SyllabusPage;